import { numberToPastelRainbowColor } from '@/javascript/color-helper';
import { Section } from '@/javascript/music-objects';
import { Card } from '@mantine/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import MidiBlocks from '../midi-blocks';

export interface ClientRectData {
	left: number;
	top: number;
	width: number;
	height: number;
}

export interface DragPreviewSectionPayload {
	section: Section;
	partIndex: number;
	rect: ClientRectData;
	dragToken?: string;
}

export interface DragPreviewInitPayload {
	sections: DragPreviewSectionPayload[];
	groupOrigin: { x: number; y: number };
	pointerOffset: { x: number; y: number };
	initialCursor: { x: number; y: number };
}

interface DragPreviewOverlayProps {
	data: DragPreviewInitPayload | null;
	cursor: { x: number; y: number } | null;
	overTimeline: boolean;
	ghostLayouts: Record<string, ClientRectData>;
	shouldReturn: boolean;
	dropCompleted: boolean;
	onReturnComplete: () => void;
	onDropAnimationEnd: () => void;
}

const TRANSITION_MS = 280;

const DragPreviewOverlay: React.FC<DragPreviewOverlayProps> = ({
	data,
	cursor,
	overTimeline,
	ghostLayouts,
	shouldReturn,
	dropCompleted,
	onReturnComplete,
	onDropAnimationEnd
}) => {
	const [hasLaunched, setHasLaunched] = useState(false);
	const [phase, setPhase] = useState<'origin' | 'free' | 'timeline' | 'return' | 'fade'>('origin');
	const lastCursorRef = useRef<{ x: number; y: number } | null>(null);
	const returnTimer = useRef<number>();
	const fadeTimer = useRef<number>();
	const lastPositionsRef = useRef<Record<string, ClientRectData>>({});

	useEffect(() => {
		if (data) {
			setHasLaunched(false);
			setPhase('origin');
			lastCursorRef.current = data.initialCursor;
			const frame = window.requestAnimationFrame(() => {
				setHasLaunched(true);
				setPhase('free');
			});
			return () => {
				window.cancelAnimationFrame(frame);
			};
		} else {
			setHasLaunched(false);
			setPhase('origin');
			lastPositionsRef.current = {};
		}
	}, [data]);

	useEffect(() => {
		if (cursor) {
			lastCursorRef.current = cursor;
		}
	}, [cursor]);

	useEffect(() => {
		if (!data || !hasLaunched) {
			return;
		}

		if (dropCompleted) {
			setPhase('fade');
			return;
		}

		if (shouldReturn) {
			setPhase('return');
			return;
		}

		if (overTimeline && Object.keys(ghostLayouts).length > 0) {
			setPhase('timeline');
		} else {
			setPhase('free');
		}
	}, [data, hasLaunched, dropCompleted, shouldReturn, overTimeline, ghostLayouts]);

	useEffect(() => {
		if (phase === 'return') {
			if (returnTimer.current) {
				window.clearTimeout(returnTimer.current);
			}
			returnTimer.current = window.setTimeout(onReturnComplete, TRANSITION_MS + 80);
		}
		return () => {
			if (phase === 'return' && returnTimer.current) {
				window.clearTimeout(returnTimer.current);
				returnTimer.current = undefined;
			}
		};
	}, [phase, onReturnComplete]);

	useEffect(() => {
		if (phase === 'fade') {
			if (fadeTimer.current) {
				window.clearTimeout(fadeTimer.current);
			}
			fadeTimer.current = window.setTimeout(onDropAnimationEnd, TRANSITION_MS + 80);
		}
		return () => {
			if (phase === 'fade' && fadeTimer.current) {
				window.clearTimeout(fadeTimer.current);
				fadeTimer.current = undefined;
			}
		};
	}, [phase, onDropAnimationEnd]);

	const relativeOffsets = useMemo(() => {
		if (!data) return {};
		const offsets: Record<string, { x: number; y: number }> = {};
		data.sections.forEach(({ section, rect, dragToken }) => {
			const key = dragToken ?? section.id;
			offsets[key] = {
				x: rect.left - data.groupOrigin.x,
				y: rect.top - data.groupOrigin.y
			};
		});
		return offsets;
	}, [data]);

	const resolveRect = (matchKey: string, fallbackRect: ClientRectData): ClientRectData => {
		if (!data) {
			return fallbackRect;
		}

		if (!hasLaunched || phase === 'origin') {
			return fallbackRect;
		}

		if (phase === 'fade') {
			return lastPositionsRef.current[matchKey] ?? fallbackRect;
		}

		if (phase === 'return') {
			return fallbackRect;
		}

		if (phase === 'timeline') {
			const target = ghostLayouts[matchKey];
			if (target) {
				return target;
			}
		}

		const pointer = cursor ?? lastCursorRef.current ?? data.initialCursor;
		const groupLeft = pointer.x - data.pointerOffset.x;
		const groupTop = pointer.y - data.pointerOffset.y;
		const offset = relativeOffsets[matchKey] ?? { x: 0, y: 0 };
		return {
			left: groupLeft + offset.x,
			top: groupTop + offset.y,
			width: fallbackRect.width,
			height: fallbackRect.height
		};
	};

	if (!data || typeof document === 'undefined') {
		return null;
	}

	const overlay = (
		<div
			style={{
				position: 'fixed',
				left: 0,
				top: 0,
				right: 0,
				bottom: 0,
				pointerEvents: 'none',
				zIndex: 9999
			}}
			data-drag-preview-root="true"
		>
			{data.sections.map((payload) => {
				const matchKey = payload.dragToken ?? payload.section.id;
				const rect = resolveRect(matchKey, payload.rect);
				lastPositionsRef.current[matchKey] = rect;
				const color = numberToPastelRainbowColor(payload.partIndex);
				const isAlignedToGhost = phase === 'timeline' && Boolean(ghostLayouts[matchKey]);
				const isUnavailable = overTimeline && !ghostLayouts[matchKey];
				const isFading = phase === 'fade';
				const moveSpeed = phase === 'free' ? 0.001 : 0.15;
				return (
					<div
						key={`${matchKey}`}
						style={{
							position: 'fixed',
							left: rect.left,
							top: rect.top,
							width: rect.width,
							height: rect.height,
							transition: `left ${moveSpeed}s ease, top ${moveSpeed}s ease, width 0.25s ease, height 0.25s ease, opacity 0.2s ease`,
							opacity: isFading ? 0 : isAlignedToGhost ? 0.5 : 1
						}}
					>
						<Card
							bg={isUnavailable ? 'gray.4' : `${color}.${isAlignedToGhost ? 2 : 4}`}
							padding={0}
							style={{
								height: '100%',
								width: '100%',
								pointerEvents: 'none',
								display: 'flex',
								alignItems: 'stretch',
								outline: '1px solid #1e1e1e',
								filter: isUnavailable ? 'grayscale(0.8)' : undefined
							}}
						>
							<MidiBlocks section={payload.section} />
						</Card>
					</div>
				);
			})}
		</div>
	);

	return ReactDOM.createPortal(overlay, document.body);
};

export default DragPreviewOverlay;
